import Link from 'next/link'
import Image from 'next/image'

interface LogoProps {
  /** Logo image URL from content management */
  logoUrl?: string
  /** Fallback text when no logo image is provided */
  logoText?: string
  /** Size variant for responsive design */
  size?: 'sm' | 'md' | 'lg'
  /** Whether the logo is clickable (links to home) */
  clickable?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Server-side logo component that handles both image and text logos
 * Uses logoUrl if provided, otherwise falls back to logoText
 */
export default function Logo({
  logoUrl,
  logoText = ' ',
  size = 'md',
  clickable = true,
  className = '',
}: LogoProps) {
  // Size mappings for different contexts
  const sizeClasses = {
    sm: {
      text: 'text-lg font-bold',
      image: 'h-8 w-auto',
      icon: 'w-5 h-5',
    },
    md: {
      text: 'text-xl font-bold',
      image: 'h-10 w-auto',
      icon: 'w-6 h-6',
    },
    lg: {
      text: 'text-2xl font-bold',
      image: 'h-12 w-auto',
      icon: 'w-8 h-8',
    },
  }

  const LogoContent = () => {
    // Show image logo if URL provided
    if (logoUrl) {
      return (
        <div className="flex items-center justify-center">
          <Image
            src={logoUrl}
            alt={logoText}
            width={200}
            height={50}
            className={`${sizeClasses[size].image} object-contain`}
            priority
            unoptimized
            sizes="(max-width: 768px) 100px, 200px"
          />
        </div>
      )
    }

    // Fallback to text logo - centered
    return (
      <div className="flex items-center justify-center space-x-2">
        <span className={`${sizeClasses[size].text} font-luxury text-primary`}>
          {logoText}
        </span>
      </div>
    )
  }

  const baseClasses = `transition-all duration-200 ${className}`

  if (clickable) {
    return (
      <Link
        href="/"
        className={`${baseClasses} rounded-lg hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50`}
        aria-label={`Go to homepage - ${logoText}`}
      >
        <LogoContent />
      </Link>
    )
  }

  return (
    <div className={baseClasses}>
      <LogoContent />
    </div>
  )
}
